/* eslint-disable no-plusplus */

import { createSelector } from 'reselect';
import Immutable, { fromJS, List } from 'immutable';
import { currentCohortPathSelector } from '../Auth/selectors';
import { selectCurrentCoursePath } from '../Courses/selectors';
import { selectCurrentPagePath } from '../Pages/selectors';
import { selectLocation } from '../location/selectors';
import {
  ROUTE_COURSE_ADMIN,
  ROUTE_COHORT_ADMIN,
  ROUTE_COURSE_CERTIFICATION,
  ROUTE_COURSE_ASSESSMENT,
} from '../routeActions';

import './injectReducers';
import { selectOpenModuleSet } from '../Courses/ModuleDisplay/selectors';

export function selectCourseNavigationInfo(state) {
  return state.CourseNavigationInfo;
}

const defaultCourseNavigationInfo = Immutable.Map();

export const selectCurrentCourseNavigationInfo = createSelector(
  selectCourseNavigationInfo,
  selectCurrentCoursePath,
  (courseNavigationLinks, currentCoursePath) =>
    currentCoursePath
      ? courseNavigationLinks.getIn(
          ['byCoursePath', currentCoursePath],
          defaultCourseNavigationInfo
        )
      : null
);

export const selectShowCollapsedModulesInCurrentCourse = createSelector(
  [selectCurrentCourseNavigationInfo],
  (courseNavigationInfo) =>
    courseNavigationInfo.get('hasLoadedNavData')
      ? courseNavigationInfo.get('showCollapsedModules')
      : null
);

const linkIsInUse = (link, currentPagePath, openModuleSet) => {
  let inUse = false;
  if (link.get('pagePath') && link.get('pagePath') === currentPagePath) {
    inUse = true;
  } else if (
    link.get('type') === 'default-module-set' &&
    openModuleSet === 'Default'
  ) {
    inUse = true;
  } else if (
    link.get('type') === 'module-set' &&
    openModuleSet === link.get('name')
  ) {
    inUse = true;
  }
  return inUse;
};

// Given a navigation link, return the subpath of the page that the
// link is to inside the course (if the link is to a page).
// The subpath is the empty string if the page path is the same as the
// course path, and null if the link is not to a page or is to a page that is
// not a subpath of the course.
export const linkCourseSubPath = (link) => {
  if (link.get('external')) {
    return null;
  } else if (link.get('type') === 'separator') {
    return null;
  } else if (link.get('type') === 'default-module-set') {
    return null;
  } else if (link.get('type') === 'module-set') {
    return null;
  }
  const coursesSplit = `/${link.get('pagePath')}`.split('/courses/');
  if (coursesSplit.length < 2) {
    return null;
  }
  const pathAfterCourses = coursesSplit[1];
  const segmentsAfterCourseCode = pathAfterCourses.split('/').slice(1);
  // Two cases - a sub path of the course path, or the course path itself
  return segmentsAfterCourseCode.join('/') || '';
};

export const selectCurrentCourseNavigationLinks = createSelector(
  [
    selectCurrentCourseNavigationInfo,
    selectCurrentPagePath,
    selectOpenModuleSet,
  ],
  (courseNavigationInfo, currentPagePath, openModuleSet) => {
    const links = courseNavigationInfo && courseNavigationInfo.get('links');
    let index = 0;
    return links
      ? links.map((link) =>
          link
            .set('inUse', linkIsInUse(link, currentPagePath, openModuleSet))
            .set('courseSubPath', linkCourseSubPath(link))
            .set('index', index++)
        )
      : new List();
  }
);

export const selectCurrentCohortAdminLinks = createSelector(
  selectCurrentCoursePath,
  (coursePath) => {
    if (!coursePath) {
      return null;
    }
    return fromJS([
      {
        name: 'General',
        action: 'general',
      },
      {
        name: 'Timeline',
        action: 'timeline',
      },
      {
        name: 'Learners',
        // Kelvin: not sure if we want to change all the "students" to "learners"
        // in the code yet. Eventually we will?
        action: 'students',
      },
      {
        name: 'Statistics',
        action: 'statistics',
      },
      {
        name: 'Advanced',
        action: 'advanced',
      },
    ]);
  }
);

export const selectCurrentCourseAdminLinks = createSelector(
  [selectCurrentCoursePath, currentCohortPathSelector],
  (coursePath, cohortPath) => {
    if (!coursePath || !cohortPath) {
      return null;
    }
    return fromJS([
      {
        name: 'Setup Wizard',
        action: 'starter',
      },
      {
        name: 'Outcomes',
        action: 'outcomes',
      },
      {
        name: 'General',
        action: 'general',
      },
      {
        name: 'Landing Page',
        action: 'landing_page',
      },
      {
        name: 'Appearance',
        action: 'appearance',
      },
      {
        name: 'Content',
        action: 'content',
      },
      {
        name: 'Groups',
        action: 'groups',
      },
      {
        name: 'Community',
        action: 'community',
      },
      {
        name: 'Roles',
        action: 'roles',
      },
      {
        name: 'Exports',
        action: 'exports',
      },
      {
        name: 'Advanced',
        action: 'advanced',
      },
    ]);
  }
);

export const selectCourseAdminExpanded = createSelector(
  selectCourseNavigationInfo,
  selectLocation,
  (navigationInfo, location) => {
    if (navigationInfo && navigationInfo.has('courseAdminExpanded')) {
      return navigationInfo.get('courseAdminExpanded');
    } else {
      return location.get('type') === ROUTE_COURSE_ADMIN;
    }
  }
);

export const selectCohortAdminExpanded = createSelector(
  selectCourseNavigationInfo,
  selectLocation,
  (navigationInfo, location) => {
    if (navigationInfo && navigationInfo.has('cohortAdminExpanded')) {
      return navigationInfo.get('cohortAdminExpanded');
    } else {
      return location.get('type') === ROUTE_COHORT_ADMIN;
    }
  }
);

export const selectCertificationExpanded = createSelector(
  selectCourseNavigationInfo,
  selectLocation,
  (navigationInfo, location) => {
    if (navigationInfo && navigationInfo.has('certificationExpanded')) {
      return navigationInfo.get('certificationExpanded');
    } else {
      return location.get('type') === ROUTE_COURSE_CERTIFICATION;
    }
  }
);

export const selectAssessmentExpanded = createSelector(
  selectCourseNavigationInfo,
  selectLocation,
  (navigationInfo, location) => {
    if (navigationInfo && navigationInfo.has('assessmentExpanded')) {
      return navigationInfo.get('assessmentExpanded');
    } else {
      return location.get('type') === ROUTE_COURSE_ASSESSMENT;
    }
  }
);

export const selectIsCurrentCourseLoadingNavData = createSelector(
  [selectCourseNavigationInfo, selectCurrentCoursePath],
  (navigationInfo, coursePath) =>
    navigationInfo.getIn(
      ['byCoursePath', coursePath, 'isLoadingNavData'],
      false
    )
);

export const selectHasLoadedCurrentCourseNavData = createSelector(
  [selectCourseNavigationInfo, selectCurrentCoursePath],
  (navigationInfo, coursePath) =>
    navigationInfo.getIn(
      ['byCoursePath', coursePath, 'hasLoadedNavData'],
      false
    )
);
